import type { UseQueryResult, useQuery } from '@tanstack/react-query';
import { upperFirst } from 'lodash-es';
import { isDefined } from 'remeda';

type TPreventUndefined = 'PreventUndefined' | false;

// TODO: This function runs on every render as it stands. It can be memoized, but only based
// on the contents of the query result, not the query result object itself, which is not referentially stable

export type UseResourceQueryResult<
  TData,
  TError,
  ResourceName extends string,
  PreventUndefined extends TPreventUndefined = false, // Use this when you are using placeholderData
> = {
  [_K in 'data' as `${ResourceName}`]: PreventUndefined extends 'PreventUndefined'
    ? TData
    : TData | undefined;
} & {
  [K in 'error' as `${ResourceName}${Capitalize<K>}`]: TError | null;
} & {
  [_K in 'isFetching' as `is${Capitalize<ResourceName>}Fetching`]: boolean;
} & {
  [_K in 'isRefetching' as `is${Capitalize<ResourceName>}Refetching`]: boolean;
} & {
  [_K in 'isLoading' as `is${Capitalize<ResourceName>}Loading`]: boolean;
} & {
  [_K in 'isInitialLoading' as `is${Capitalize<ResourceName>}InitialLoading`]: boolean;
} & {
  [K in 'refetch' as `${K}${Capitalize<ResourceName>}`]: UseQueryResult<TData, TError>['refetch'];
};

export function addResourceNameToQueryResult<
  TData,
  TError,
  ResourceName extends string,
  PreventUndefined extends TPreventUndefined = false,
>(
  resourceName: ResourceName,
  queryResult: ReturnType<typeof useQuery<TData, TError>>,
  ...fallback: PreventUndefined extends 'PreventUndefined' ? [fallback: TData] : []
): UseResourceQueryResult<TData, TError, ResourceName, PreventUndefined> {
  const resourceNameCapitalised = upperFirst(resourceName);

  const { data, error, isFetching, isRefetching, isLoading, isPlaceholderData, refetch } =
    queryResult;

  return {
    [resourceName]: isDefined(data) ? data : fallback[0],
    [`${resourceName}Error`]: error,
    [`is${resourceNameCapitalised}Fetching`]: isFetching,
    [`is${resourceNameCapitalised}Refetching`]: isRefetching,
    [`is${resourceNameCapitalised}Loading`]: isLoading,
    [`is${resourceNameCapitalised}InitialLoading`]: isFetching && isPlaceholderData,
    [`refetch${resourceNameCapitalised}`]: refetch,
  } as UseResourceQueryResult<TData, TError, ResourceName, PreventUndefined>;
}
